import React, { useEffect } from 'react'
import { lockBodyScroll } from '@/utils/bodyScrollLock'
import {
  ORDER_EDITING_DISABLED_MESSAGE,
  ORDER_EDITING_DISABLED_PHONE,
  ORDER_EDITING_DISABLED_TITLE,
} from '@/config/maintenance'

/**
 * Shown instead of the edit dialogs while ORDER_EDITING_DISABLED is set.
 *
 * Deliberately self-contained rather than a variant of ConfirmModal: this is a
 * hotfix, and keeping it separate means the whole thing can be deleted in one
 * go rather than leaving an unused prop behind on a shared component.
 *
 * A notice, not a decision, so there is one button and no cancel.
 */
interface Props {
  isOpen: boolean
  onClose: () => void
}

const OrderEditingDisabledModal: React.FC<Props> = ({ isOpen, onClose }) => {
  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose()
    }

    let releaseScroll: (() => void) | undefined
    if (isOpen) {
      document.addEventListener('keydown', handleEscape)
      releaseScroll = lockBodyScroll()
    }

    return () => {
      document.removeEventListener('keydown', handleEscape)
      releaseScroll?.()
    }
  }, [isOpen, onClose])

  // Split on the number so it can be tapped. If it ever stops appearing in
  // the message, `after` is empty and the sentence still reads correctly
  // rather than losing half of itself.
  const [before, after = ''] = ORDER_EDITING_DISABLED_MESSAGE.split(
    ORDER_EDITING_DISABLED_PHONE,
  )

  if (!isOpen) return null

  return (
    <div
      className="modal-backdrop"
      onClick={(e) => { if (e.target === e.currentTarget) onClose() }}
    >
      <div className="modal-container confirm-modal" role="alertdialog" aria-modal="true">
        <div className="modal-header">
          <h2>{ORDER_EDITING_DISABLED_TITLE}</h2>
          <button className="modal-close-floating" onClick={onClose} aria-label="Close">
            <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="modal-content">
          <p className="confirm-message">
            {before}
            <a href={`tel:${ORDER_EDITING_DISABLED_PHONE.replace(/\s/g, '')}`}>
              {ORDER_EDITING_DISABLED_PHONE}
            </a>
            {after}
          </p>
        </div>

        <div className="modal-footer">
          <button className="btn-confirm" onClick={onClose} autoFocus>
            OK
          </button>
        </div>
      </div>
    </div>
  )
}

export default OrderEditingDisabledModal
